import React from 'react';
import { Box, FormControl, FormHelperText, MenuItem, OutlinedInput, Select, TextField } from '@mui/material';
import type { AddressValue, FormField } from '../../../types';
import { getFormFieldInputType, formatPhoneNumber, formatPostalCode, STATE_PROVINCE_OPTIONS } from '../../forms/formFieldOptions';

interface FormFieldInputProps {
  field: FormField;
  value: string | AddressValue;
  onChange: (value: string | AddressValue) => void;
  onBlur?: () => void;
  inputId?: string;
  inputName?: string;
  error?: string | null;
  sx?: Record<string, unknown>;
}

function FormFieldInput({
  field,
  value,
  onChange,
  onBlur,
  inputId,
  inputName,
  error,
  sx,
}: FormFieldInputProps): JSX.Element {
  if (field.type === 'address') {
    const helperId = `${inputId ?? `form-field-${field.id}`}-helper`;
    const address = typeof value === 'object' && value !== null
      ? value
      : { addressLine1: '', addressLine2: '', city: '', stateProvince: '', postalCode: '' };
    const parts: Array<[Exclude<keyof AddressValue, 'country'>, string]> = [
      ['addressLine1', 'Address Line 1'], ['addressLine2', 'Address Line 2'], ['city', 'City'],
      ['stateProvince', 'State / Province'], ['postalCode', 'Postal Code'],
    ];
    const serializedAddress = parts
      .map(([part]) => address[part].trim())
      .filter(Boolean)
      .join(', ');
    const handleAddressPartChange = (part: Exclude<keyof AddressValue, 'country'>, nextValue: string): void => {
      onChange({ ...address, [part]: part === 'postalCode' ? formatPostalCode(nextValue) : nextValue });
    };

    return (
      <Box
        onBlur={(event) => {
          const nextTarget = event.relatedTarget;
          if (!(nextTarget instanceof Node) || !event.currentTarget.contains(nextTarget)) onBlur?.();
        }}
        sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}
      >
        <input type="hidden" id={inputId} name={inputName} value={serializedAddress} readOnly />
        {parts.map(([part, label], index) => (
          <TextField key={part} id={`${inputId ?? `form-field-${field.id}`}-${part}`} name={`${inputName ?? `form-field-${field.id}`}-${part}`} label={label} value={address[part]} onChange={(event) => handleAddressPartChange(part, event.target.value)} required={field.isRequired && part !== 'addressLine2'} fullWidth size="small" inputProps={{ 'data-address-part': part, 'aria-describedby': helperId }} InputProps={{ sx }} {...(part === 'stateProvince' ? { select: true } : {})} sx={index < 2 ? { gridColumn: { sm: '1 / -1' } } : undefined} error={Boolean(error)}>
            {part === 'stateProvince' && <MenuItem value="" disabled={field.isRequired}>Select state / province</MenuItem>}
            {part === 'stateProvince' ? STATE_PROVINCE_OPTIONS.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>) : null}
          </TextField>
        ))}
        <FormHelperText id={helperId} error={Boolean(error)} aria-hidden={!error || undefined} title={error || undefined} sx={{ gridColumn: '1 / -1', mt: '5px', minHeight: '1.25em', maxHeight: '1.25em', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', fontSize: '0.75rem', lineHeight: 1.25, visibility: error ? 'visible' : 'hidden' }}>{error || '\u00A0'}</FormHelperText>
      </Box>
    );
  }

  if (field.type === 'dropdown') {
    const helperId = `${inputId ?? `form-field-${field.id}`}-helper`;
    return (
      <FormControl fullWidth error={Boolean(error)}>
        <Select
          className="cla-quiz-field"
          data-quiz-field-type="dropdown"
          id={inputId}
          name={inputName}
          aria-describedby={helperId}
          value={value ?? ''}
          displayEmpty
          SelectDisplayProps={{ 'aria-label': field.label }}
          onChange={(event) => onChange(String(event.target.value))}
          onBlur={onBlur}
          sx={sx}
        >
          <MenuItem value="" disabled={field.isRequired}>{field.placeholder || 'Select an option'}</MenuItem>
          {[...(field.options ?? [])].sort((a, b) => a.sortOrder - b.sortOrder).map((option) => (
            <MenuItem key={option.id} value={option.value}>{option.text}</MenuItem>
          ))}
        </Select>
        <FormHelperText id={helperId} error={Boolean(error)} aria-hidden={!error || undefined} title={error || undefined} sx={{ mt: '5px', minHeight: '1.25em', maxHeight: '1.25em', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', fontSize: '0.75rem', lineHeight: 1.25, visibility: error ? 'visible' : 'hidden' }}>{error || '\u00A0'}</FormHelperText>
      </FormControl>
    );
  }

  return (
    <>
      <OutlinedInput
        className="cla-quiz-field"
        data-quiz-field-type={field.type}
        id={inputId}
        name={inputName}
        aria-describedby={`${inputId ?? `form-field-${field.id}`}-helper`}
        type={getFormFieldInputType(field.type)}
        fullWidth
        multiline={field.type === 'textarea'}
        rows={field.type === 'textarea' ? 4 : undefined}
        value={value}
        onChange={(event) => onChange(field.type === 'phone' ? formatPhoneNumber(event.target.value) : event.target.value)}
        onBlur={onBlur}
        placeholder={field.placeholder}
        error={Boolean(error)}
        sx={sx}
      />
      <FormHelperText id={`${inputId ?? `form-field-${field.id}`}-helper`} error={Boolean(error)} aria-hidden={!error || undefined} title={error || undefined} sx={{ mt: '5px', minHeight: '1.25em', maxHeight: '1.25em', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', fontSize: '0.75rem', lineHeight: 1.25, visibility: error ? 'visible' : 'hidden' }}>{error || '\u00A0'}</FormHelperText>
    </>
  );
}

export default FormFieldInput;
